// Ported from @openreplay/tracker-assist 6.0.0 (heraclescorp/openreplay-assist@75886445c4231b1a0df699e1c3725da4042293aa)
// Copyright (c) 2022 Asayer, Inc. MIT licensed, see ./LICENSE
import type { App } from '@openreplay/tracker'
import type { Socket } from 'socket.io-client'
import { connect } from 'socket.io-client'
import { OpenReplayAssistStartOutcome } from '../openReplayAssistStartOutcome'
import { OpenReplayAssistSocketEvent } from './openReplayAssistSocketEvent'
import { filterOpenReplayMessagesForLiveView } from './openReplayLiveViewMessageFilter'

const REQUIRED_TRACKER_VERSION = '8.0.0'
const TRACKER_RESTART_DELAY_MS = 500
const CONNECT_TIMEOUT_MS = 10_000

export class OpenReplayLiveViewAssist {
    private socket: Socket | null = null
    // connects whenever the tracker starts, like the fork it replaces, until stop()
    private isAssistRequested = true
    private isRestartingTrackerForAgents = false
    private agentRestartTimeoutId: ReturnType<typeof setTimeout> | null = null
    private agentRestartStart: Promise<void> | null = null
    private readonly connectedAgentIds = new Set<string>()

    constructor(private readonly app: App) {
        if (document.hidden !== undefined) {
            app.attachEventListener(document, 'visibilitychange', () => this.emit(OpenReplayAssistSocketEvent.UPDATE_SESSION, { active: !document.hidden }), false, false)
        }
        const titleNode = document.querySelector('title')
        const titleObserver = titleNode ? new MutationObserver(() => this.emit(OpenReplayAssistSocketEvent.UPDATE_SESSION, { pageTitle: document.title })) : null

        app.attachStartCallback(() => {
            if (this.isRestartingTrackerForAgents) {
                return
            }
            if (this.isAssistRequested) {
                this.connect()
            }
            if (titleNode && titleObserver) {
                titleObserver.observe(titleNode, { subtree: true, characterData: true, childList: true })
            }
        })
        app.attachStopCallback(() => {
            if (this.isRestartingTrackerForAgents) {
                return
            }
            this.disconnect()
            titleObserver?.disconnect()
        })
        app.attachCommitCallback((messages) => {
            if (this.connectedAgentIds.size === 0) {
                return
            }
            this.emit(
                OpenReplayAssistSocketEvent.MESSAGES,
                filterOpenReplayMessagesForLiveView(messages, (nodeId) => this.app.nodes.getNode(nodeId) || undefined),
            )
        })
    }

    get forcingTrackerRestart(): boolean {
        return this.isRestartingTrackerForAgents
    }

    /** Resolves once the socket has connected, so a blocked or failing socket is never reported as sharing. */
    public async start(): Promise<OpenReplayAssistStartOutcome> {
        this.isAssistRequested = true
        if (!this.app.active()) {
            return OpenReplayAssistStartOutcome.WAITING_FOR_TRACKER_START
        }
        this.connect()
        return await this.waitForSocketConnection()
    }

    /**
     * Disconnects and stays disconnected, even across tracker restarts, until start() is called again. A pending snapshot
     * restart is cancelled and one already starting is waited out, so it can't restart the tracker in a session the caller is leaving.
     */
    public async stop(): Promise<void> {
        this.isAssistRequested = false
        this.disconnect()
        if (this.agentRestartTimeoutId !== null) {
            clearTimeout(this.agentRestartTimeoutId)
            this.agentRestartTimeoutId = null
            this.isRestartingTrackerForAgents = false
        }
        await this.agentRestartStart
    }

    private emit(eventName: OpenReplayAssistSocketEvent, data?: unknown): void {
        this.socket?.emit(eventName, { meta: { tabId: this.app.getTabId() }, data })
    }

    private connect(): void {
        if (this.socket) {
            return
        }
        const sessionId = this.app.getSessionID()
        if (!sessionId) {
            this.app.debug.error('No session ID')
            return
        }
        const tabId = this.app.getTabId()
        const socket = connect(this.app.getHost(), {
            path: '/ws-assist/socket',
            query: {
                peerId: `${this.app.getProjectKey()}-${sessionId}-${tabId}`,
                identity: 'session',
                tabId,
                sessionInfo: JSON.stringify({ pageTitle: document.title, active: true, ...this.app.getSessionInfo(), metadata: {} }),
            },
            transports: ['websocket'],
        })
        socket.on(OpenReplayAssistSocketEvent.NEW_AGENT, (agentId: string) => {
            this.connectedAgentIds.add(agentId)
            this.restartTrackerForAgents()
        })
        socket.on(OpenReplayAssistSocketEvent.AGENTS_CONNECTED, (agentIds: string[]) => {
            agentIds.forEach((agentId) => this.connectedAgentIds.add(agentId))
            this.restartTrackerForAgents()
        })
        socket.on(OpenReplayAssistSocketEvent.AGENT_DISCONNECTED, (agentId: string) => {
            this.connectedAgentIds.delete(agentId)
        })
        socket.on(OpenReplayAssistSocketEvent.NO_AGENT, () => {
            this.connectedAgentIds.clear()
        })
        socket.on('disconnect', () => {
            this.connectedAgentIds.clear()
        })
        this.socket = socket
    }

    private waitForSocketConnection(): Promise<OpenReplayAssistStartOutcome> {
        const socket = this.socket
        if (!socket) {
            return Promise.resolve(OpenReplayAssistStartOutcome.CONNECTION_FAILED)
        }
        if (socket.connected) {
            return Promise.resolve(OpenReplayAssistStartOutcome.CONNECTED)
        }
        return new Promise((resolve) => {
            const finish = (outcome: OpenReplayAssistStartOutcome): void => {
                clearTimeout(timeoutId)
                socket.off('connect', onConnect)
                socket.off('connect_error', onConnectError)
                resolve(outcome)
            }
            const onConnect = (): void => finish(OpenReplayAssistStartOutcome.CONNECTED)
            const onConnectError = (): void => finish(OpenReplayAssistStartOutcome.CONNECTION_FAILED)
            const timeoutId = setTimeout(() => finish(OpenReplayAssistStartOutcome.CONNECTION_FAILED), CONNECT_TIMEOUT_MS)
            socket.on('connect', onConnect)
            socket.on('connect_error', onConnectError)
        })
    }

    private disconnect(): void {
        this.connectedAgentIds.clear()
        if (!this.socket) {
            return
        }
        this.socket.disconnect()
        this.socket = null
    }

    // Restarting makes the tracker send a full snapshot of the page, which the newly joined agents need
    private restartTrackerForAgents(): void {
        if (this.isRestartingTrackerForAgents) {
            // the restart already in progress sends one snapshot for every agent that joined meanwhile
            return
        }
        this.isRestartingTrackerForAgents = true
        this.app.stop()
        this.agentRestartTimeoutId = setTimeout(() => {
            this.agentRestartTimeoutId = null
            this.agentRestartStart = this.app
                .start()
                .then(() => undefined)
                .catch((error) => this.app.debug.error(error))
                .finally(() => {
                    this.isRestartingTrackerForAgents = false
                    this.agentRestartStart = null
                })
        }, TRACKER_RESTART_DELAY_MS)
    }
}

export const openReplayLiveViewAssistPlugin =
    () =>
    (app: App | null): OpenReplayLiveViewAssist | undefined => {
        if (app === null) {
            return undefined
        }
        if (!app.checkRequiredVersion(REQUIRED_TRACKER_VERSION)) {
            app.notify.warn(`OpenReplay Live View Assist needs @openreplay/tracker@${REQUIRED_TRACKER_VERSION} or newer`)
            return undefined
        }
        return new OpenReplayLiveViewAssist(app)
    }
